import React, { useEffect, useRef } from "react";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import type { MentionCategory, MentionSuggestion } from "../shared/mcp-chat-types";

interface McpChatMentionDropdownProps {
  suggestions: MentionSuggestion[];
  selectedIndex: number;
  position: { top: number; left: number } | null;
  onSelect: (suggestion: MentionSuggestion) => void;
  onHover: (index: number) => void;
}

const CATEGORY_COLORS: Record<MentionCategory, string> = {
  component: "#7c5cf5",
  datasource: "#0891b2",
  action: "#059669",
  screen: "#d97706",
  error: "#ef4444",
  language: "#2563eb"
};

const CATEGORY_ICONS: Record<MentionCategory, { path: string; fill: boolean }> = {
  component: {
    path: "M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z",
    fill: true
  },
  datasource: {
    path: "M4 7v10c0 2 3.2 3 8 3s8-1 8-3V7M4 7c0 2 3.2 3 8 3s8-1 8-3M4 7c0-2 3.2-3 8-3s8 1 8 3m0 5c0 2-3.2 3-8 3s-8-1-8-3",
    fill: false
  },
  action: {
    path: "M13 10V3L4 14h7v7l9-11h-7z",
    fill: true
  },
  screen: {
    path: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
    fill: false
  },
  error: {
    path: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z",
    fill: false
  },
  language: {
    path: "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129",
    fill: false
  }
};

function CategoryIcon({ category, color }: { category: MentionCategory; color: string }): JSX.Element {
  const iconDef = CATEGORY_ICONS[category];
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill={iconDef.fill ? color : "none"}
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={iconDef.path} />
    </svg>
  );
}

const McpChatMentionDropdown: React.FC<McpChatMentionDropdownProps> = ({
  suggestions,
  selectedIndex,
  position,
  onSelect,
  onHover
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (!position || suggestions.length === 0) return null;

  return (
    <div
      className="mcp-mention-dropdown"
      ref={listRef}
      role="listbox"
      aria-label="Mention suggestions"
      style={{ bottom: position.top, left: position.left }}
    >
      {suggestions.map((suggestion, idx) => {
        const isSelected = idx === selectedIndex;
        const color = CATEGORY_COLORS[suggestion.category];
        return (
          <button
            key={`${suggestion.category}-${suggestion.entityId}`}
            ref={isSelected ? selectedRef : undefined}
            className={`mcp-mention-dropdown__item ${OVERFLOW_REVEAL_HOST_CLASS} ${isSelected ? "mcp-mention-dropdown__item--selected" : ""}`}
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(suggestion)}
            onMouseEnter={() => onHover(idx)}
          >
            <CategoryIcon category={suggestion.category} color={color} />
            <span className="mcp-mention-dropdown__name">
              <KuikaOverflowText>{suggestion.displayName}</KuikaOverflowText>
            </span>
            <span className="mcp-mention-dropdown__secondary">{suggestion.secondaryText}</span>
            <span className="mcp-mention-dropdown__badge" style={{ "--badge-color": color } as React.CSSProperties}>
              {suggestion.category}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default McpChatMentionDropdown;
